// @flow
import classnames from 'classnames';
import { isEmpty, upperFirst } from 'lodash';
import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useRouter } from 'Client/router/useRouter';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink.jsx';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import {
  FACET_QUERY_PARAMS,
  navigateToSearchPage,
} from 'Components/widgets/facet-search/FacetSearch.utils';
import { useTrackQueryChange } from 'Components/widgets/facet-search/useTrackQueryChange';
import { useTrackSearchResults } from 'Components/widgets/facet-search/useTrackSearchResults';
import { t } from 'Src/i18n';
import { getKidsValidationState } from 'Store/faceting/faceting.selector';
import { changeSearchQuery } from 'Store/faceting/facetingSearch.action';
import {
  getDepartureDate,
  getTripDuration,
  getDepartureCity,
} from 'Store/trip-criteria/tripCriteria.selector';
import { getKids } from 'Store/trip-information/storeHelper';
import { RESORT_PRODUCT_TYPE } from 'Store/utils/autocompleteItemHelper';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';
import { getSearchMappedTypeForLabelGA } from 'Tracking/utils';

import {
  GlobalSearchSuggestionsList,
  SUGGESTION_TYPES,
  type SearchSuggestionsProps,
} from './GlobalSearchSuggestionsList';
import { GlobalSearchSuggestionsGrid, MAX_GRID_SUGGESTIONS } from './GlobalSearchSuggestionsGrid';

type SuggestionBlockTitleProps = {
  title: string,
};

const SuggestionsBlockTitle = ({ title }: SuggestionBlockTitleProps) => (
  <h3 className="my-6 lg:my-8 font-bold font-sans leading-tight text-base text-blue uppercase">
    {title}
  </h3>
);

export const SearchSuggestions = (props: SearchSuggestionsProps) => {
  const { isDesktop } = useContext(ResponsiveContext);
  const trackQueryChange = useTrackQueryChange();
  const trackSearchResults = useTrackSearchResults();
  const departureCity = useSelector(getDepartureCity);
  const departureDate = useSelector(getDepartureDate);
  const duration = useSelector(getTripDuration);
  const kids = useSelector(getKids);
  const kidsValidationState = useSelector(getKidsValidationState);
  const dispatch = useDispatch();
  const { location } = useRouter<{}, SearchQueryParams>();

  const suggestionsBlocks = [
    {
      id: SUGGESTION_TYPES.COUNTRY,
      types: [SUGGESTION_TYPES.AREA, SUGGESTION_TYPES.COUNTRY],
      title: t('faceting.autocomplete.destinationsTitle'),
      linkLabel: t('faceting.autocomplete.moreDestinationsLabel'),
      linkURL: t('faceting.autocomplete.moreDestinationsURL'),
      width: isDesktop ? 'w-1/4' : 'w-full',
      order: 1,
    },
    {
      id: SUGGESTION_TYPES.ACTIVITY,
      types: [SUGGESTION_TYPES.ACTIVITY],
      type: SUGGESTION_TYPES.ACTIVITY,
      title: t('faceting.autocomplete.activitiesTitle'),
      linkLabel: t('faceting.autocomplete.moreActivitiesLabel'),
      linkURL: t('faceting.autocomplete.moreActivitiesURL'),
      width: isDesktop ? 'w-1/4' : 'w-full',
      order: isDesktop ? 2 : 3,
    },
    {
      id: SUGGESTION_TYPES.RESORT,
      types: [SUGGESTION_TYPES.RESORT],
      title: t('faceting.autocomplete.productsTitle'),
      linkLabel: t('faceting.autocomplete.moreProductsLabel'),
      linkURL: '/s',
      width: isDesktop ? 'w-1/2' : 'w-full',
      order: isDesktop ? 3 : 2,
    },
  ];

  const trackingPayload = {
    categoryGA: 'layer',
    searchReferrer: 'layer',
  };

  return (
    <div
      id="search-suggestions"
      className={classnames('flex m-auto max-w-site', {
        'mb-15 flex-wrap': !isDesktop,
      })}
    >
      {suggestionsBlocks.map(block => {
        const blockSuggestions = props.autocompleteResults.filter(
          result => !!result.label && block.types.includes(result.type),
        );
        const SuggestionsBlock =
          block.id === SUGGESTION_TYPES.RESORT
            ? GlobalSearchSuggestionsGrid
            : GlobalSearchSuggestionsList;
        return !isDesktop && isEmpty(blockSuggestions) ? null : (
          <div
            key={`search-suggestions-${block.id}`}
            className={classnames(block.width, {
              'mb-6 border-gray-light border-t-1': !isDesktop,
            })}
            style={{ order: block.order }}
          >
            {block.id !== SUGGESTION_TYPES.RESORT ? (
              <SuggestionsBlockTitle title={block.title} />
            ) : (
              <div className="flex justify-between items-strech">
                <SuggestionsBlockTitle title={block.title} />
                {blockSuggestions.length > MAX_GRID_SUGGESTIONS && (
                  <TrackClick
                    as={DynamicLink}
                    className="my-6 lg:my-8 leading-tight font-bold text-sm text-gray underline"
                    isExternalLink={false}
                    onClick={() => {
                      props.clearGlobalSearch();
                    }}
                    trackingData={{
                      ...trackingPayload,
                      actionGA: 'search',
                      labelGA: `viewMore_${getSearchMappedTypeForLabelGA(block.id)}`,
                    }}
                    url={`/s?search=${props.autocompleteQuery}`}
                  >
                    {upperFirst(block.linkLabel)}
                  </TrackClick>
                )}
              </div>
            )}
            {isEmpty(blockSuggestions) ? (
              <p className="mt-0 mb-6 leading-tight text-xl lg:text-2xl text-gray-light">
                {upperFirst(t('faceting.autocomplete.noResult'))}
              </p>
            ) : (
              <SuggestionsBlock
                {...props}
                blockSuggestions={blockSuggestions}
                isDesktop={isDesktop}
                onClick={(query: SearchQuery) => {
                  props.clearGlobalSearch();

                  if (query.type === RESORT_PRODUCT_TYPE) {
                    trackQueryChange(query, {
                      ...trackingPayload,
                      internalSearchEngineNbResults: 'redirection',
                    });
                    trackSearchResults({
                      internalSearchEngineNbResults: 'redirection',
                      searchRequestType: 'Autocomplete',
                      searchTerms: query.label,
                      searchResorts: [],
                    });
                  } else {
                    dispatch(changeSearchQuery(query));
                    trackQueryChange(query, trackingPayload);
                    navigateToSearchPage({
                      criteriaKeysToPersist: FACET_QUERY_PARAMS,
                      location,
                      searchCriteria: {
                        query,
                        departureCity,
                        departureDate,
                        duration,
                        keywords: '',
                        kids,
                        kidsValidationState,
                      },
                      searchPageRouteName: 'search',
                    });
                  }
                }}
              />
            )}
            {block.linkLabel && block.linkURL && block.id !== SUGGESTION_TYPES.RESORT ? (
              <TrackClick
                as={DynamicLink}
                className="font-bold text-sm text-gray underline"
                isExternalLink={false}
                onClick={props.clearGlobalSearch}
                trackingData={{
                  categoryGA: 'layer',
                  actionGA: 'search',
                  labelGA: `viewMore_${block.id}`,
                  searchReferrer: 'layer',
                }}
                url={block.linkURL}
              >
                {upperFirst(block.linkLabel)}
              </TrackClick>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
